/* eslint-disable lit-a11y/click-events-have-key-events */
import { css, html, LitElement, type TemplateResult } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import * as signalR from '@microsoft/signalr';
import { when } from 'lit/directives/when.js';
import { packageSyncUrl, validationApiBaseUrl } from '../constants';
import { prismStyles } from '../styles/prism';
import { baseStyles } from '../styles/base';

type ValidationMessage = {
  messageType: number;
  message: string | TemplateResult;
};

@customElement('umb-market-validate')
export class UmbMarketValidate extends LitElement {
  @state()
  private _connectionId!: string;

  @state()
  private _messages: Array<ValidationMessage> = [];

  @state()
  private _tabs: Array<{ label: string; id: string }> = [
    {
      label: 'Validate package ID',
      id: 'validatePackageId',
    },
    {
      label: 'Validate package JSON',
      id: 'validatePackageJson',
    },
  ];

  @state()
  private _activeTab = '';

  @query('#validatePackageForm')
  private _validatePackageForm!: HTMLFormElement;

  @query('#validateJsonForm')
  private _validateJsonForm!: HTMLFormElement;

  #connection!: signalR.HubConnection;

  #messageTypes: Array<string> = [
    'default',
    'info',
    'error',
    'success',
    'warning',
    'code',
    'validationFailed',
    'validationComplete',
  ];

  // assumes package is valid until we hear otherwise
  // if invalid, triggering sync is disabled
  #validationFailed = false;

  #syncRequested = false;

  async connectedCallback() {
    super.connectedCallback();

    this._activeTab = this._tabs[0].label;

    this.#connection = new signalR.HubConnectionBuilder()
      .withUrl(`${validationApiBaseUrl}validationHub/`, {
        withCredentials: false,
      })
      .build();

    this.#connection
      .start()
      .then(async () => {
        this._connectionId = await this.#connection.invoke('getConnectionId');
      })
      .catch(err => this._messages.push({ message: err, messageType: 2 }));

    this.#connection.on(
      'validationMessage',
      async (message: ValidationMessage) => {
        // track failed validation - disables trigger sync button
        if (message.messageType === 6) {
          this.#validationFailed = true;
          return;
        }

        console.log(message);

        // track complete
        if (message.messageType === 7) {
          if (this.#validationFailed === false) {
            this._messages.push({
              message: html`Nice one! Package passes validation, do you want to
                run the data synchronisation task?
                <umb-market-button
                  style="display:block;margin-top:var(--s-spacer)"
                  @click=${this.#requestSync}
                >
                  Yes please, request package sync
                </umb-market-button>`,
              messageType: this.#messageTypes.indexOf('validationComplete'),
            });

            this.requestUpdate();
          }

          return;
        }

        this._messages.push(message);
        this.requestUpdate();

        this.shadowRoot?.querySelector('li.alert:last-child')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#connection.stop();
  }

  #submitValidatePackageForm() {
    this._validatePackageForm.requestSubmit();
  }

  #submitValidateJsonForm() {
    this._validateJsonForm.requestSubmit();
  }

  async #validateJson(e: SubmitEvent) {
    e.preventDefault();

    this._messages = [];

    const form = e.target as HTMLFormElement;
    if (!form || !form.checkValidity()) return;

    const formData = new FormData(form);
    const packageId = formData.get('packageId') as string;
    const json = formData.get('json') as string;

    await fetch(`${validationApiBaseUrl}api/v1.0/validateJson`, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'Connection-ID': this._connectionId,
      },
      body: JSON.stringify({
        id: packageId,
        json,
      }),
    });
  }

  async #validatePackage(e: SubmitEvent) {
    e.preventDefault();

    this._messages = [];
    this.#validationFailed = false;
    this.#syncRequested = false;

    const form = e.target as HTMLFormElement;
    if (!form || !form.checkValidity()) return;

    const formData = new FormData(form);
    const packageId = formData.get('packageId') as string;
    const version = (formData.get('version') as string) ?? '';

    await fetch(
      `${validationApiBaseUrl}api/v1.0/validatePackage/${packageId}?version=${version}&connectionId=${this._connectionId}`,
      {
        headers: {
          'Connection-ID': this._connectionId,
        },
      }
    );
  }

  async #requestSync() {
    if (this.#syncRequested) return;

    this.#syncRequested = true;

    const formData = new FormData(this._validatePackageForm);
    const packageId = formData.get('packageId');

    const response = await fetch(packageSyncUrl, {
      method: 'POST',
      body: JSON.stringify({
        PackageId: packageId,
      }),
    });

    if (response.status === 202) {
      this._messages.push({
        message: `Sync request has been queued, update will take a few minutes.`,
        messageType: this.#messageTypes.indexOf('success'),
      });
    } else if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const message = retryAfter
        ? `Sync request has been throttled, retry in ${retryAfter} seconds.`
        : 'Sync request has been throttled, please try again later.';

      this._messages.push({
        message,
        messageType: this.#messageTypes.indexOf('warning'),
      });
    } else {
      this._messages.push({
        message: `Unexpected response from sync request, received status code ${response.status}`,
        messageType: this.#messageTypes.indexOf('error'),
      });
    }

    // render updated messages
    this.requestUpdate();
  }

  #handleTabClick(e: PointerEvent) {
    const elm = e.currentTarget as HTMLElement;
    const activeTab = this._tabs.find(
      t => t.label === elm.getAttribute('label')
    );

    this._tabs.forEach(t => {
      const tab = this.shadowRoot?.getElementById(t.id);
      if (!tab) {
        return;
      }

      tab.style.display = tab.id === activeTab!.id ? 'block' : 'none';
    });

    this._messages = [];
  }

  render() {
    return html`
      <h3 class="page-title">Package validation tools</h3>
      <uui-tab-group>
        ${this._tabs.map(
          tab =>
            html`<uui-tab
              @click=${this.#handleTabClick}
              label="${tab.label}"
              ?active="${tab.label === this._activeTab}"
            ></uui-tab>`
        )}
      </uui-tab-group>

      <div id="${this._tabs[0].id}" class="tab-content">
        <ul>
          <li>Finds the package on NuGet</li>
          <li>Checks for the 'umbraco-marketplace' tag</li>
          <li>
            Checks for a valid ProjectUrl and ensures the URL is accessible
          </li>
          <li>
            Checks for the package owner json file reference and ensures the
            file is accessible, if it exists
          </li>
          <li>Validates the JSON against the schema</li>
          <li>Performs additional validation not possible from schema</li>
          <li>
            Checks for a package owner README file and ensures the file is
            accessible, if it exists
          </li>
        </ul>
        <uui-form>
          <form
            id="validatePackageForm"
            name="validatePackageForm"
            @submit=${this.#validatePackage}
          >
            <uui-form-layout-item>
              <uui-label for="packageId" slot="label"
                >Package ID (required)</uui-label
              >
              <div>
                <uui-input
                  label="Package ID"
                  id="packageId"
                  name="packageId"
                  required
                ></uui-input>
              </div>
            </uui-form-layout-item>
            <uui-form-layout-item>
              <uui-label for="version" slot="label">Package version</uui-label>
              <div>
                <uui-input
                  label="Package version"
                  id="version"
                  name="version"
                ></uui-input>
              </div>
            </uui-form-layout-item>
            <div class="flex-row">
              <umb-market-button @click=${this.#submitValidatePackageForm}>
                Validate package ID
              </umb-market-button>
              <small>Connection ID: ${this._connectionId}</small>
            </div>
          </form>
        </uui-form>
      </div>
      <div id="${this._tabs[1].id}" class="tab-content" style="display:none">
        <ul>
          <li>Validates the JSON against the schema</li>
          <li>Performs additional validation not possible from schema</li>
          <li>
            Checks for a package owner README file and ensures the file is
            accessible, if it exists
          </li>
        </ul>
        <uui-form>
          <form
            id="validateJsonForm"
            name="validateJsonForm"
            @submit=${this.#validateJson}
          >
            <uui-form-layout-item>
              <uui-label for="packageId" slot="label"
                >Package ID (required)</uui-label
              >
              <div>
                <uui-input
                  label="Package ID"
                  id="packageId"
                  name="packageId"
                  required
                ></uui-input>
              </div>
            </uui-form-layout-item>
            <uui-form-layout-item>
              <uui-label for="json" slot="label">JSON</uui-label>
              <div>
                <uui-textarea
                  label="JSON"
                  id="json"
                  name="json"
                  required
                ></uui-textarea>
              </div>
            </uui-form-layout-item>
            <div class="flex-row">
              <umb-market-button @click=${this.#submitValidateJsonForm}>
                Validate JSON
              </umb-market-button>
              <small>Connection ID: ${this._connectionId}</small>
            </div>
          </form></uui-form
        >
      </div>

      <ul class="alert-list">
        ${this._messages.map(m => {
          if (m.messageType === 5) {
            return html`<li class="alert ${this.#messageTypes[m.messageType]}">
              <pre><code class="language-json">${m.message}</code></pre>
            </li> `;
          }
          return html`<li class="alert ${this.#messageTypes[m.messageType]}">
            <span
              >${when(
                m.messageType !== 7,
                () => `${this.#messageTypes[m.messageType].toUpperCase()}:`
              )}
              ${m.message}</span
            >
          </li>`;
        })}
      </ul>
    `;
  }

  static styles = [
    baseStyles,
    prismStyles,
    css`
      :host {
        max-width: 800px;
        margin: 0 auto;
        display: block;

        --uui-textarea-min-height: 250px;
      }
      div {
        display: flex;
        flex-direction: column;
      }

      .flex-row {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }

      .alert-list {
        list-style-type: none;
        padding: 0;
      }

      span {
        z-index: 1;
        position: relative;
      }

      .warning {
        background-color: var(--uui-color-warning);
        color: var(--uui-color-warning-contrast);
      }
      .success {
        background-color: var(--uui-color-positive);
        color: var(--uui-color-positive-contrast);
      }
      .error {
        background-color: var(--uui-color-danger);
        color: var(--uui-color-danger-contrast);
      }
      .info {
        background-color: var(--uui-color-default);
        color: var(--uui-color-default-contrast);
      }

      .validationComplete {
        background-color: var(--uui-color-default);
        color: var(--uui-color-default-contrast);
      }

      .code {
        background-color: var(--uui-color-surface);
      }

      code {
        padding: 1px 4px;
        border-radius: 4px;
        white-space: pre-wrap !important;
        display: block;
      }

      pre {
        background-color: white !important;
        border-radius: 4px;
        position: relative;
        z-index: 1;
      }

      umb-market-button {
        --button-width: auto;
      }

      umb-market-button + umb-market-button {
        margin-left: var(--s-spacer);
        margin-right: auto;
      }

      hr {
        margin: 30px 0;
        border-style: dashed;
        border-color: var(--uui-color-current);
      }

      .tab-content {
        padding: var(--s-spacer) 0;
      }
    `,
  ];
}
